import { useState, useRef, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

function absolutizeHtml(html, baseUrl) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const base = new URL(baseUrl);
    // Only update <link rel="stylesheet" href="/..."></link>
    doc.querySelectorAll('link[rel="stylesheet"][href]').forEach(el => {
      const href = el.getAttribute('href');
      if (href.startsWith('/')) {
        el.setAttribute('href', base.origin + href);
      }
    });
    // Also update <a href="/..."></a> and <a href="?..."></a>
    doc.querySelectorAll('a[href]').forEach(el => {
      const href = el.getAttribute('href');
      if (href.startsWith('/')) {
        // For path-based links, use origin + href (don't include current pathname)
        el.setAttribute('href', base.origin + href);
      } else if (href.startsWith('?')) {
        // For query parameter links, use origin + pathname + href
        el.setAttribute('href', base.origin + base.pathname + href);
      }
    });
    // Also update <script src="/..."></script>
    doc.querySelectorAll('script[src]').forEach(el => {
      const src = el.getAttribute('src');
      if (src.startsWith('/')) {
        el.setAttribute('src', base.origin + src);
      }
    });
    return doc.documentElement.innerHTML;
  } catch (e) {
    return html;
  }
}

export const useHtmlFetching = () => {
  const [htmlContent, setHtmlContent] = useState('');
  const [fetchSuccess, setFetchSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [currentPreviewUrl, setCurrentPreviewUrl] = useState(null);
  const [history, setHistory] = useState([]);
  const contentRef = useRef(null);
  const shadowRef = useRef(null);

  const handleGetHtml = async (newUrl = null, addToHistory = true) => {
    setLoading(true);
    setFetchError('');
    setFetchSuccess(false);
    try {
      const fetchUrl = newUrl || currentPreviewUrl;
      if (addToHistory && newUrl && newUrl !== currentPreviewUrl) {
        setHistory(prev => [...prev, currentPreviewUrl]);
        setCurrentPreviewUrl(newUrl);
      }

      // Check if environment variables are set
      if (!SUPABASE_URL || !SUPABASE_KEY) {
        console.error('Supabase environment variables not configured');
        setFetchError('Supabase configuration missing. Please check your .env file.');
        return;
      }

      console.log('Fetching HTML from:', fetchUrl);
      console.log('Using Supabase URL:', SUPABASE_URL);
      
      // const requestUrl = `${SUPABASE_URL}/functions/v1/fetch-html`;
      const requestUrl = 'https://worker-fetch-content-html.pablo-2b9.workers.dev/';
      console.log('Making request to:', requestUrl);
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${SUPABASE_KEY}`
        },
        body: JSON.stringify({ url: fetchUrl }),
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Response data:', data);
      
      if (!data.html) throw new Error('No HTML returned from the server');
      
      const processedHtml = absolutizeHtml(data.html, fetchUrl);
      setHtmlContent(processedHtml);
      setFetchSuccess(true);
      if (contentRef.current) contentRef.current.scrollTop = 0;
    } catch (err) {
      console.error('Error fetching HTML:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      
      if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        setFetchError('CORS error: The server is not allowing requests from this domain. Please check your Supabase Edge Function CORS configuration.');
      } else {
        setFetchError(`Could not fetch HTML from the provided URL: ${err.message}`);
      }
      setHtmlContent('');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (history.length > 0) {
      const previousUrl = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      setCurrentPreviewUrl(previousUrl);
      handleGetHtml(previousUrl, false);
    }
  };

  // Inject HTML into Shadow DOM for style isolation
  useEffect(() => {
    console.log('Injecting into Shadow DOM. htmlContent:', htmlContent);
    if (shadowRef.current) {
      let shadow;
      if (shadowRef.current.shadowRoot) {
        shadow = shadowRef.current.shadowRoot;
        shadow.innerHTML = ''; // Clear previous content
      } else {
        shadow = shadowRef.current.attachShadow({ mode: 'open' });
      }
      // For debugging: if htmlContent is empty, inject a test string
      if (!htmlContent) {
        shadow.innerHTML = '<div style="color:red">[Debug] No HTML content to display</div>';
      } else {
        shadow.innerHTML = htmlContent;
      }
      // Removed verbose injected HTML log
    }
  }, [htmlContent]);

  return {
    htmlContent,
    fetchSuccess,
    loading,
    fetchError,
    currentPreviewUrl,
    history,
    contentRef,
    shadowRef,
    handleGetHtml,
    handleBack,
    setCurrentPreviewUrl
  };
}; 